import { IconButton, type IconButtonProps } from '@chakra-ui/react/button';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { chakra, type HTMLChakraProps, type RecipeProps, useRecipe } from '@chakra-ui/react/styled-system';
import {
  Children,
  cloneElement,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type Ref,
} from 'react';

import { type BitkitIconComponent } from '../../icons';
import opensPopup from '../../utilities/opensPopup';
import BitkitLabelTooltip, { type BitkitLabelTooltipProps } from '../BitkitLabelTooltip/BitkitLabelTooltip';

type BitkitIconButtonOmittedFromButton =
  | 'aria-label'
  | 'asChild'
  | 'children'
  | 'colorPalette'
  | 'disabled'
  | 'loading'
  | 'loadingText'
  | 'spinner'
  | 'spinnerPlacement';

interface BitkitIconButtonCommonProps {
  icon: BitkitIconComponent;
  label: string;
  /**
   * Overrides for the label tooltip. It is off by default when this control opens a popup — a menu,
   * select or popover trigger — where the panel is the affordance and a tooltip would hover over it.
   * Pass `{ disabled: false }` to bring it back. The accessible name comes from `label` either way.
   */
  tooltipProps?: Partial<Omit<BitkitLabelTooltipProps, 'children' | 'text'>>;
}

interface BitkitIconButtonAsButtonProps
  extends BitkitIconButtonCommonProps, Omit<IconButtonProps, BitkitIconButtonOmittedFromButton> {
  asChild?: false;
  href?: undefined;
  isExternal?: undefined;
  state?: 'disabled' | 'loading' | 'skeleton';
}

interface BitkitIconButtonAsAnchorProps
  extends
    BitkitIconButtonCommonProps,
    Omit<HTMLChakraProps<'a'>, 'aria-label' | 'children' | 'colorPalette'>,
    RecipeProps<'button'> {
  asChild?: false;
  href: string;
  isExternal?: boolean;
  state?: 'disabled' | 'skeleton';
}

interface BitkitIconButtonAsChildProps
  extends BitkitIconButtonCommonProps, Omit<IconButtonProps, BitkitIconButtonOmittedFromButton> {
  asChild: true;
  children: ReactElement;
  href?: undefined;
  isExternal?: undefined;
  state?: 'disabled' | 'loading' | 'skeleton';
}

export type BitkitIconButtonProps =
  BitkitIconButtonAsButtonProps | BitkitIconButtonAsAnchorProps | BitkitIconButtonAsChildProps;

// The forwarded ref is typed as HTMLElement to cover all three modes (button, anchor, slot/asChild),
// since asChild can render any element (router Link, custom wrappers, etc.). Inner narrow casts
// satisfy the wrapped Chakra components without affecting the consumer-visible ref shape.
const BitkitIconButton = forwardRef<HTMLElement, BitkitIconButtonProps>((props, ref) => {
  const buttonRecipe = useRecipe({ key: 'button' });
  const Icon = props.icon;
  // An unset `size` reaches us as `undefined` — the `button` recipe's `lg` default lives in CSS,
  // not on the prop — so resolve it here rather than letting it fall to the 16px branch.
  const iconSize = (props.size ?? 'lg') === 'lg' ? '24' : '16';
  const isDisabled = props.state === 'disabled';
  const isSkeleton = props.state === 'skeleton';
  const isPopupTrigger = opensPopup(props);

  // --- Slot mode (asChild) ---
  if (props.asChild) {
    const { asChild: _asChild, children, icon: _icon, label, state, tooltipProps, ...buttonRest } = props;
    const onlyChild = Children.only(children) as ReactElement<{ 'aria-label'?: string }>;
    const iconButtonElement = (
      <IconButton
        asChild
        aria-label={label}
        disabled={isDisabled}
        loading={state === 'loading'}
        ref={ref as Ref<HTMLButtonElement>}
        {...buttonRest}
      >
        {cloneElement(onlyChild, { 'aria-label': onlyChild.props['aria-label'] ?? label }, <Icon size={iconSize} />)}
      </IconButton>
    );
    return (
      <BitkitLabelTooltip text={label} disabled={isPopupTrigger} {...tooltipProps}>
        {isSkeleton ? (
          <Skeleton asChild loading>
            {iconButtonElement}
          </Skeleton>
        ) : (
          iconButtonElement
        )}
      </BitkitLabelTooltip>
    );
  }

  // --- Anchor mode (href) ---
  if (props.href !== undefined) {
    const {
      css,
      href,
      icon: _icon,
      isExternal,
      label,
      onClick,
      onKeyDown,
      rel,
      size,
      state: _state,
      target,
      tooltipProps,
      variant,
      ...anchorRest
    } = props;
    const effectiveTarget = isExternal ? '_blank' : target;
    const effectiveRel = isExternal ? (rel ? `${rel} noreferrer noopener` : 'noreferrer noopener') : rel;
    const handleClick = isDisabled
      ? (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }
      : onClick;
    const handleKeyDown = isDisabled
      ? (e: KeyboardEvent<HTMLAnchorElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      : onKeyDown;
    const anchorElement = (
      <chakra.a
        ref={ref as Ref<HTMLAnchorElement>}
        {...anchorRest}
        aria-disabled={isDisabled || undefined}
        aria-label={label}
        css={[buttonRecipe({ size, variant }), { paddingBlock: 0, paddingInline: 0 }, css]}
        href={isDisabled ? undefined : href}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        rel={effectiveRel}
        target={effectiveTarget}
      >
        <Icon size={iconSize} />
      </chakra.a>
    );
    return (
      <BitkitLabelTooltip text={label} disabled={isPopupTrigger} {...tooltipProps}>
        {isSkeleton ? (
          <Skeleton asChild loading>
            {anchorElement}
          </Skeleton>
        ) : (
          anchorElement
        )}
      </BitkitLabelTooltip>
    );
  }

  // --- Button mode (default) ---
  const { icon: _icon, label, state, tooltipProps, ...buttonRest } = props;
  return (
    <BitkitLabelTooltip text={label} disabled={isPopupTrigger} {...tooltipProps}>
      <Skeleton asChild loading={isSkeleton}>
        <IconButton
          aria-label={label}
          disabled={isDisabled}
          loading={state === 'loading'}
          ref={ref as Ref<HTMLButtonElement>}
          {...buttonRest}
        >
          <Icon size={iconSize} />
        </IconButton>
      </Skeleton>
    </BitkitLabelTooltip>
  );
});

BitkitIconButton.displayName = 'BitkitIconButton';

export default BitkitIconButton;
